/**
 * RetentionQuizOverlay.jsx — Optional 3-question retention quiz that
 * fires after every Book Series episode finish.
 *
 * Feb 12 2026 user spec: *"Ask users if they want to earn coins by
 * answering 3 questions to test retention... award 2 coins per correct
 * answer and ask 3 questions in book series."*
 *
 * Flow:
 *   1. First-time prompt — "Want to earn coins by answering 3 retention
 *      questions after each chapter?" (Yes / No). Records the user's
 *      choice via /api/profile/retention-quiz.
 *   2. If enabled, immediately generates the quiz via /api/.../generate
 *      and shows the 3 questions.
 *   3. On submit, shows results card with green/red ticks + coin reward.
 *
 * One attempt only — second mount on the same (user, episode) returns
 * the locked result from the backend.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Loader2, Coins, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE = {
  PROMPT: 'prompt',
  LOADING: 'loading',
  QUIZ: 'quiz',
  RESULT: 'result',
};

const RetentionQuizOverlay = ({ open, seriesId, episodeId, onClose }) => {
  const [phase, setPhase] = useState(PHASE.LOADING);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !seriesId || !episodeId) return;
    let cancelled = false;
    (async () => {
      setPhase(PHASE.LOADING);
      setResult(null);
      // 1) Check user's setting — show the prompt if they've never
      //    answered the "want quizzes?" question.
      try {
        const sr = await authFetch(`${BACKEND_URL}/api/profile/retention-quiz`);
        const s = await sr.json();
        if (cancelled) return;
        if (!s.prompted) {
          setPhase(PHASE.PROMPT);
          return;
        }
        if (!s.enabled) {
          // User opted out — close without doing anything.
          onClose?.();
          return;
        }
        // 2) Setting is ON → generate the quiz immediately.
        await beginQuiz();
      } catch {
        if (!cancelled) onClose?.();
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, seriesId, episodeId]);

  const beginQuiz = async () => {
    setPhase(PHASE.LOADING);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/text-series/${seriesId}/episodes/${episodeId}/retention-quiz/generate`,
        { method: 'POST' },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not generate quiz');
        onClose?.();
        return;
      }
      if (d.already_submitted) {
        // Lock-state — show the locked result without coins re-awarded.
        setResult({
          score: d.score, total: d.total, coins_awarded: 0,
          already_submitted: true,
        });
        setPhase(PHASE.RESULT);
        return;
      }
      setQuestions(d.questions || []);
      setAnswers(new Array((d.questions || []).length).fill(-1));
      setPhase(PHASE.QUIZ);
    } catch {
      toast.error('Network error');
      onClose?.();
    }
  };

  // First-time prompt → save the user's pick (enabled=true|false).
  const choosePrompt = async (enabled) => {
    try {
      await authFetch(`${BACKEND_URL}/api/profile/retention-quiz`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled, prompted: true }),
      });
    } catch { /* never block on the toggle save */ }
    if (enabled) {
      await beginQuiz();
    } else {
      toast.message('Retention quiz disabled. You can re-enable it in Settings.');
      onClose?.();
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/text-series/${seriesId}/episodes/${episodeId}/retention-quiz/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ answers }),
        },
      );
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not submit quiz');
        return;
      }
      setResult(d);
      setPhase(PHASE.RESULT);
      if (d.coins_awarded > 0) {
        toast.success(`+${d.coins_awarded} coins · ${d.score}/${d.total} retention bonus`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[75] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
      data-testid="retention-quiz-overlay"
    >
      <div className="w-full max-w-lg bg-[#12131A] border border-amber-400/30 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <h2 className="font-display text-lg font-black text-white tracking-tight">
              Retention bonus<span className="text-amber-300">.</span>
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center"
            data-testid="retention-quiz-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
        </header>

        {/* Body */}
        <div className="px-5 py-5">
          {phase === PHASE.LOADING && (
            <div className="py-10 text-center" data-testid="retention-quiz-loading">
              <Loader2 className="w-6 h-6 text-amber-300 animate-spin mx-auto mb-3" />
              <p className="text-sm text-white/60">Cooking up your 3 questions…</p>
            </div>
          )}

          {phase === PHASE.PROMPT && (
            <div className="space-y-4 text-center" data-testid="retention-quiz-prompt">
              <Coins className="w-10 h-10 text-amber-300 mx-auto" />
              <h3 className="font-display text-2xl font-black text-white">
                Earn extra coins?
              </h3>
              <p className="text-sm text-white/70 leading-relaxed">
                After every Book Series episode, we can ask you <strong className="text-white">3 quick questions</strong> to test
                what stuck. <strong className="text-amber-200">+2 coins for each correct answer</strong> (up to +6 per episode).
              </p>
              <p className="text-[11px] text-white/40">
                You can turn this off anytime in Reader Settings.
              </p>
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => choosePrompt(false)}
                  className="flex-1 rounded-full border border-white/15 hover:border-white/30 text-white/80 text-xs font-black uppercase tracking-widest px-4 py-2.5"
                  data-testid="retention-quiz-prompt-decline"
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={() => choosePrompt(true)}
                  className="flex-1 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-black uppercase tracking-widest px-4 py-2.5"
                  data-testid="retention-quiz-prompt-accept"
                >
                  Yes — quiz me
                </button>
              </div>
            </div>
          )}

          {phase === PHASE.QUIZ && (
            <div className="space-y-5" data-testid="retention-quiz-questions">
              <p className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-black text-center">
                {questions.length} questions · +2 coins per correct
              </p>
              {questions.map((q, qi) => (
                <fieldset
                  key={qi}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-3"
                  data-testid={`retention-quiz-q-${qi}`}
                >
                  <legend className="px-2 text-[10px] uppercase tracking-widest text-amber-300 font-black">
                    Q{qi + 1}
                  </legend>
                  <p className="text-sm text-white font-semibold mb-3">{q.question}</p>
                  <div className="space-y-1.5">
                    {q.options.map((opt, oi) => (
                      <label
                        key={oi}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs cursor-pointer transition ${
                          answers[qi] === oi
                            ? 'border-amber-400/60 bg-amber-400/10 text-white'
                            : 'border-white/5 hover:border-white/20 text-white/80'
                        }`}
                        data-testid={`retention-quiz-q-${qi}-opt-${oi}`}
                      >
                        <input
                          type="radio"
                          name={`rq-${qi}`}
                          checked={answers[qi] === oi}
                          onChange={() => {
                            const next = [...answers]; next[qi] = oi; setAnswers(next);
                          }}
                          className="accent-amber-400"
                        />
                        <span>{opt}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
              <button
                type="button"
                onClick={submit}
                disabled={submitting || answers.some((a) => a < 0)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-black px-6 py-3 text-sm uppercase tracking-widest"
                data-testid="retention-quiz-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Submit · earn up to {questions.length * 2} coins
              </button>
            </div>
          )}

          {phase === PHASE.RESULT && result && (
            <div className="space-y-4 text-center" data-testid="retention-quiz-result">
              {result.coins_awarded > 0 ? (
                <Coins className="w-12 h-12 text-amber-300 mx-auto" />
              ) : (
                <XCircle className="w-12 h-12 text-rose-300/80 mx-auto" />
              )}
              <h3 className="font-display text-3xl font-black text-white tabular-nums">
                {result.score} <span className="text-white/50 text-lg">/ {result.total}</span>
              </h3>
              {result.coins_awarded > 0 ? (
                <p className="text-amber-200 text-sm" data-testid="retention-quiz-result-coins">
                  +{result.coins_awarded} coins added to your balance
                </p>
              ) : (
                <p className="text-white/60 text-sm">
                  {result.already_submitted ? 'You already attempted this quiz.' : 'No coins this time — give the next one a fresh read.'}
                </p>
              )}

              {/* Review — show which answers were right/wrong */}
              {Array.isArray(result.correct) && questions.length > 0 && (
                <div className="text-left space-y-2 pt-3" data-testid="retention-quiz-review">
                  {questions.map((q, qi) => {
                    const ok = result.correct?.[qi];
                    const correctIdx = result.answer_indexes?.[qi];
                    return (
                      <div
                        key={qi}
                        className={`rounded-lg border p-3 text-xs ${ok ? 'border-emerald-400/30 bg-emerald-400/5' : 'border-rose-400/30 bg-rose-400/5'}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          {ok ? <CheckCircle2 className="w-3 h-3 text-emerald-300" /> : <XCircle className="w-3 h-3 text-rose-300" />}
                          <span className="text-[10px] uppercase tracking-widest font-black text-white/60">Q{qi + 1}</span>
                        </div>
                        <p className="text-white/80 mb-1">{q.question}</p>
                        <p className="text-[10px] text-white/50">
                          Correct: <span className="text-emerald-200">{q.options[correctIdx]}</span>
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                type="button"
                onClick={onClose}
                className="w-full rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-black uppercase tracking-widest px-6 py-2.5"
                data-testid="retention-quiz-result-close"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetentionQuizOverlay;
